/**
 * @fileoverview Client action to re-execute an expired chat run result.
 *
 * When a historical chat message references a run whose Arrow result has passed
 * its retention window, this re-runs the stored SQL server-side (`runs.reexecute`),
 * seeds the fresh payload into the cache when returned inline, and records the
 * old→new runId remap so mounted result tables rehydrate against the new run.
 */

import { trpc } from "~/libs/trpc.lib";
import { seedRunPayload } from "../datasets/rift-dataset-payload.query";
import { recordRunRemap } from "./run-rift";

/** Decodes a base64 string to raw bytes in the browser. */
const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/**
 * Re-executes an expired run and remaps it to the fresh run. Resolves once the
 * remap is recorded (and the inline payload seeded, if present); the mounted
 * `useRunRift` consumer then re-derives and hydrates against the new runId.
 */
export const reexecuteRun = async (oldRunId: string): Promise<void> => {
  const result = await trpc.runs.reexecute.mutate({ runId: oldRunId });
  if (result.inlineArrow) {
    await seedRunPayload(result.runId, base64ToBytes(result.inlineArrow));
  }
  recordRunRemap(oldRunId, result.runId);
};
